import { PlusIcon } from "@heroicons/react/24/outline"
import { PencilIcon } from "@heroicons/react/24/solid"
import React, { useState } from "react"
import { twMerge } from "tailwind-merge"
import { useMutation } from "urql"

import AcceptTermsModal from "../../components/shared/AcceptTermsModal"
import DatePicker from "../../components/shared/DatePicker"
import { EditableValue, Input } from "../../components/shared/Inputs"
import { Box, Divider } from "../../components/shared/Layout"
import { RadioWithLabel } from "../../components/shared/RadioButtons"
import { useProfileStatus } from "../../contexts/ProfileStatusContext"
import { useToast } from "../../contexts/ToastContext"
import { resolveImagePickerHandle } from "../../utils/resolveImagePickerHandle"
import ImagePickerV2 from "../WebsiteBuilderV2/components/shared/ImagePickerV2"

const UPDATE_INFO_MUTATION = `
  mutation UpdatePersonalSettings($name: String, $title: String, $gender: String, $dob: String, $customGender: String) {
    updatePersonalSettings(name: $name, title: $title, gender: $gender, dob: $dob, customGender: $customGender) {
      result
      errors
      user { id name gender dob customGender }
    }
  }
`

const UPDATE_USER_PHOTO_MUTATION = `
  mutation UpdateUserPhoto($handle: String!) {
    updateUserPhoto(handle: $handle) {
      result
      errors
      photoUrl
    }
  }
`

export default function PersonalInformationUser({ user, imagePickerPractice = {} }) {
  const [userState, setUserState] = useState(user)
  const [lastSavedUserValue, setLastSavedUserValue] = useState(user)
  const [openField, setOpenField] = useState(null)
  const [photoUrlValue, setPhotoUrlValue] = useState(user.defaultPhotoUrl || "")
  const { profileStatusData } = useProfileStatus()

  const [{ fetching }, updatePersonalSettings] = useMutation(UPDATE_INFO_MUTATION)
  const [{ fetching: photoFetching }, updateUserPhoto] = useMutation(UPDATE_USER_PHOTO_MUTATION)

  const allFetching = fetching || photoFetching

  const { showToast } = useToast()

  const onSave = () => {
    updatePersonalSettings(Object.fromEntries(Object.entries(userState).filter(([, v]) => !!v))).then((result) => {
      setOpenField(null)
      if (result.error) {
        console.error(result) // eslint-disable-line no-console
        setUserState(lastSavedUserValue)
        showToast({
          type: "error",
          content: result.error.message.replace("[GraphQL]", "")
        })
      } else {
        const payload = result.data?.updatePersonalSettings
        if (payload?.result === "failure") {
          setUserState(lastSavedUserValue)
          const msg = payload.errors?.filter(Boolean).join(". ")
          showToast({
            type: "error",
            content: msg || "We couldn’t save your settings. Please try again."
          })
        } else if (payload?.user) {
          setLastSavedUserValue(payload.user)
          showToast("Your settings have been saved.")
        }
      }
    })
  }

  const handleImageSelect = ({ handle, filestackPhoto }) => {
    updateUserPhoto({
      handle: resolveImagePickerHandle({ handle, filestackPhoto })
    }).then((result) => {
      setOpenField(null)
      if (result?.data?.updateUserPhoto?.result === "success") {
        setPhotoUrlValue(result.data.updateUserPhoto.photoUrl)
        showToast("Your profile photo has been saved.")
      } else {
        console.error(result) // eslint-disable-line no-console
        let errorMessage = "There was an error saving your profile photo. Please try again later or contact support."
        if (result.data?.updateUserPhoto?.errors) errorMessage += ` ${result.data.updateUserPhoto.errors}`
        showToast({
          type: "error",
          content: errorMessage
        })
      }
    })
  }

  const onOpened = (fieldName) => {
    setOpenField(fieldName)
  }

  return (
    <div className="mt-4 w-[600px] md:w-full">
      <Box className="rounded-b-none">
        <div className={twMerge("mb-4 flex items-center justify-center", allFetching || openField ? "opacity-50" : "")}>
          {photoUrlValue.indexOf("defaults") > -1 || !photoUrlValue ? (
            <ImagePickerV2
              practice={imagePickerPractice}
              onImageSelect={handleImageSelect}
              fetching={photoFetching}
              showCurateTab={false}
              trigger={
                <div
                  className={twMerge(
                    "flex cursor-pointer flex-col items-center justify-center gap-2",
                    allFetching || openField ? "pointer-events-none" : ""
                  )}>
                  <div className="flex size-20 items-center justify-center rounded-full bg-teal/[0.12]">
                    <PlusIcon className="size-6 text-teal" />
                  </div>
                  <div className="font-bold text-teal">Upload headshot</div>
                </div>
              }
            />
          ) : (
            <div className="relative flex size-32 items-center justify-center">
              <img className="size-full rounded-xl object-cover" src={photoUrlValue} alt="Profile headshot" />
              <ImagePickerV2
                practice={imagePickerPractice}
                onImageSelect={handleImageSelect}
                fetching={photoFetching}
                showCurateTab={false}
                currentImageUrl={photoUrlValue}
                trigger={
                  <div
                    className={twMerge(
                      "absolute -right-5 flex size-10 cursor-pointer items-center justify-center rounded-full border border-gray bg-white",
                      allFetching || openField ? "pointer-events-none" : ""
                    )}>
                    <PencilIcon className="size-5 text-gray-dark" />
                  </div>
                }
              />
            </div>
          )}
        </div>
        <EditableValue
          name="Name"
          value={userState.name}
          saveDisabled={userState.name === lastSavedUserValue.name || userState.name?.length < 2}
          disabled={allFetching || (openField && openField !== "Name")}
          onOpened={onOpened}
          onSave={onSave}
          onCancel={() => {
            setOpenField(null)
            setUserState(lastSavedUserValue)
          }}>
          <Input
            name="Name"
            value={userState.name || ""}
            onChange={(e) => setUserState((values) => ({ ...values, name: e.target.value }))}
          />
        </EditableValue>
        <Divider />
        <EditableValue
          name="Title"
          hint="Set what your professional title is. Pro-tip: Keep this as short and clear as possible so it's easily understood by clients."
          value={userState.title}
          saveDisabled={userState.title === lastSavedUserValue.title || userState.title?.length < 2}
          disabled={allFetching || (openField && openField !== "Title")}
          onOpened={onOpened}
          onSave={onSave}
          onCancel={() => {
            setOpenField(null)
            setUserState(lastSavedUserValue)
          }}>
          <Input
            name="Title"
            placeholder="Ex: Licensed Massage Therapist"
            value={userState.title || ""}
            onChange={(e) => setUserState((values) => ({ ...values, title: e.target.value }))}
          />
        </EditableValue>
        <Divider />
        <EditableValue
          name="Gender"
          value={(userState.gender === "Custom" ? userState.customGender : userState.gender) || ""}
          saveDisabled={userState.gender === lastSavedUserValue.gender}
          disabled={allFetching || (openField && openField !== "Gender")}
          onOpened={onOpened}
          onSave={onSave}
          onCancel={() => {
            setOpenField(null)
            setUserState(lastSavedUserValue)
          }}>
          <div className="flex gap-4">
            <RadioWithLabel
              value="Female"
              name="gender"
              label="Female"
              checked={userState.gender === "Female"}
              onChange={(e) => setUserState({ ...userState, gender: e.target.value })}
            />
            <RadioWithLabel
              value="Male"
              name="gender"
              label="Male"
              checked={userState.gender === "Male"}
              onChange={(e) => setUserState({ ...userState, gender: e.target.value })}
            />
            <RadioWithLabel
              value="Custom"
              name="gender"
              label="Custom"
              checked={userState.gender === "Custom"}
              onChange={(e) => setUserState({ ...userState, gender: e.target.value })}
            />
          </div>
          {userState.gender === "Custom" && (
            <Input
              className="mt-4"
              name="Custom gender"
              value={userState.customGender || ""}
              onChange={(e) => setUserState((values) => ({ ...values, customGender: e.target.value }))}
            />
          )}
        </EditableValue>
        <Divider />
        <EditableValue
          name="Date of birth"
          value={userState.dob ? new Date(userState.dob).toDateString() : null}
          saveDisabled={userState.dob === lastSavedUserValue.dob}
          disabled={allFetching || (openField && openField !== "Date of birth")}
          onOpened={onOpened}
          onSave={onSave}
          onCancel={() => {
            setOpenField(null)
            setUserState(lastSavedUserValue)
          }}>
          <DatePicker
            selected={userState.dob ? new Date(userState.dob) : ""}
            onChange={(date) => {
              setUserState((values) => ({ ...values, dob: date }))
            }}
          />
        </EditableValue>
      </Box>
      <AcceptTermsModal showModal={profileStatusData.showTermsModal} />
    </div>
  )
}
